import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { GridLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  buscarSumarizacaoNotasFiscaisApuracao,
  exportarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import BotaoExportacaoProgresso from "../../../components/BotaoExportacaoProgresso";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import SelectFiltro from "../components/SelectFiltro";
import { fmtMesAno, formatarCnpj, sumarizacaoEmAndamento } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";
import {
  usePermissoes,
  CHAVES_APURACOES_EXPORT,
} from "../../../config/Context/PermissoesContext";

const TIPO_OPCOES = [
  { value: "", label: "Todos" },
  { value: "Entrada", label: "Entrada" },
  { value: "Saída", label: "Saída" },
];

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };

function competenciaYm(competencia) {
  return String(competencia || "").slice(0, 7);
}

function calcularTotais(linhas) {
  return linhas.reduce(
    (acc, linha) => {
      if (linha.tipo === "Entrada") {
        acc.qtdEntrada += linha.qtdNotas;
        acc.valorEntrada += linha.valorTotal;
      } else if (linha.tipo === "Saída") {
        acc.qtdSaida += linha.qtdNotas;
        acc.valorSaida += linha.valorTotal;
      }
      return acc;
    },
    { qtdEntrada: 0, valorEntrada: 0, qtdSaida: 0, valorSaida: 0 }
  );
}

export default function AbaNotasFiscais({ apuracaoId, onTotaisChange, bare }) {
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const podeExportar = pode(CHAVES_APURACOES_EXPORT);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [cnpj, setCnpj] = useState("");
  const [infoSnack, setInfoSnack] = useState({ open: false, msg: "", severity: "info" });
  const [cnpjFiltro, setCnpjFiltro] = useState("");
  const [competenciaFiltro, setCompetenciaFiltro] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarSumarizacaoNotasFiscaisApuracao(apuracaoId);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setCnpj(res.apuracao?.cnpj || "");
      setLoading(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [apuracaoId]);

  /** TXM-418 — apuração ainda não suporta grupo econômico multi-CNPJ (motor de apuração é
   * TXM-601, trabalho futuro); o filtro existe pra equalizar com o protótipo mas hoje só
   * tem a própria empresa como opção. */
  const cnpjOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...(cnpj ? [{ value: cnpj, label: formatarCnpj(cnpj) }] : []),
    ],
    [cnpj]
  );

  const competenciasDisponiveis = useMemo(() => {
    const set = new Set(linhas.map((linha) => competenciaYm(linha.competencia)));
    return Array.from(set).sort();
  }, [linhas]);

  const competenciaOpcoes = useMemo(
    () => [
      { value: "", label: "Todas" },
      ...competenciasDisponiveis.map((ym) => ({ value: ym, label: fmtMesAno(ym) })),
    ],
    [competenciasDisponiveis]
  );

  const linhasFiltradas = useMemo(() => {
    let base = linhas;
    if (cnpjFiltro && cnpjFiltro !== cnpj) {
      base = [];
    }
    if (competenciaFiltro) {
      base = base.filter((linha) => competenciaYm(linha.competencia) === competenciaFiltro);
    }
    if (tipoFiltro) {
      base = base.filter((linha) => linha.tipo === tipoFiltro);
    }
    return [...base].sort((a, b) => {
      const cmp = competenciaYm(a.competencia).localeCompare(competenciaYm(b.competencia));
      return cmp !== 0 ? cmp : String(a.tipo).localeCompare(String(b.tipo));
    });
  }, [linhas, cnpj, cnpjFiltro, competenciaFiltro, tipoFiltro]);

  const totais = useMemo(() => calcularTotais(linhasFiltradas), [linhasFiltradas]);

  useEffect(() => {
    onTotaisChange?.(totais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totais]);

  // Enfileira e acompanha o job até o download (antes só enfileirava e o arquivo nunca
  // chegava ao usuário) — ver Detalhe/hooks/useExportacaoAssincrona.js.
  const exportacao = useExportacaoAssincrona({
    apuracaoId,
    chave: "lancamentos",
    enfileirar: () =>
      exportarSumarizacaoApuracao(apuracaoId, { tipo: "lancamentos", formato: "csv" }),
    onFinalizar: ({ severity, msg }) => setInfoSnack({ open: true, msg, severity }),
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro) {
    const emAndamento = sumarizacaoEmAndamento(erro);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={emAndamento ? "info" : "error"}>
          {emAndamento
            ? "Sumarização em processamento — os dados aparecerão aqui assim que for concluída."
            : erro}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <ApuracaoCardShell
        bare={bare}
        conteudoComScrollProprio
        Icone={ReceiptLongIcon}
        titulo="Volume de notas por competência"
        tituloSx={{ fontSize: "1.25rem" }}
        acaoHeader={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {linhasFiltradas.length} registros no filtro
            </Typography>
            <BotaoExportacaoProgresso
              progresso={exportacao}
              rotulo="Exportar CSV"
              variant="contained"
              size="small"
              disabled={!podeExportar}
              onClick={() => exportacao.iniciar()}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
            />
          </Box>
        }
        filtros={
          <>
            <SelectFiltro label="CNPJ" value={cnpjFiltro} onChange={setCnpjFiltro} options={cnpjOpcoes} />
            <SelectFiltro label="Competência" value={competenciaFiltro} onChange={setCompetenciaFiltro} options={competenciaOpcoes} />
            <SelectFiltro label="Tipo" value={tipoFiltro} onChange={setTipoFiltro} options={TIPO_OPCOES} />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setCnpjFiltro("");
                setCompetenciaFiltro("");
                setTipoFiltro("");
              }}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
            >
              Limpar filtros
            </Button>
          </>
        }
        paginacao={
          linhasFiltradas.length > 0 && (
            <Pagination
              paginationKey="aba-notas-fiscais"
              data={linhasFiltradas}
              filteredData={linhasPagina}
              setFilteredData={setLinhasPagina}
            />
          )
        }
      >
        {linhasFiltradas.length === 0 ? (
          <p className={styles.emptyMsg}>Nenhuma competência encontrada para esta apuração.</p>
        ) : (
          // Sem padding lateral/wrapper extra — a tabela toca as bordas do card diretamente
          // (só o padding de cada célula dá o respiro), igual à tela de Apurações
          // (ApuracoesListagem/styles.module.css .tableSection): com o wrapper teria uma
          // faixa em branco entre a última coluna e a borda do card onde a linha de cada
          // registro não chegaria até o fim.
          <div style={{ margin: "8px 0 0", overflow: "auto" }}>
          <table className={tableStyles.accordion_table} style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "18%" }} />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">CNPJ</th>
                <th scope="col">Competência</th>
                <th scope="col">Tipo</th>
                <th scope="col" style={NUM_TH}>Quantidade de notas</th>
                <th scope="col" style={NUM_TH}>Valor total</th>
                <th scope="col" style={{ textAlign: "center" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhasPagina.map((linha) => (
                <tr key={`${linha.competencia}-${linha.tipo}`}>
                  <td>{formatarCnpj(cnpj)}</td>
                  <td>{fmtMesAno(competenciaYm(linha.competencia))}</td>
                  <td>{linha.tipo}</td>
                  <td style={NUM_TD}>{linha.qtdNotas}</td>
                  <td style={NUM_TD}>{formatarMoedaBRL(linha.valorTotal)}</td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      type="button"
                      className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                      onClick={() =>
                        navigate(
                          `/relatorios/apuracoes/${apuracaoId}/detalhe/icms/lancamentos-notas-fiscais` +
                            `?competencia=${competenciaYm(linha.competencia)}&tipo=${encodeURIComponent(linha.tipo)}`
                        )
                      }
                    >
                      Detalhes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </ApuracaoCardShell>

      <Snackbar
        open={infoSnack.open}
        autoHideDuration={4000}
        onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
      </Snackbar>
    </>
  );
}
